import { useMemo, memo } from 'react';
import loadable from '@loadable/component';
import Header from './components/Header';
import Disclaimer from './components/Disclaimer';
import { OneColumn } from '@/components/Layout';
import { Grid, Text, Box, Bones, useTheme } from '@simplywallst/ui-core';
import { useReduxStateForCommunityIdeas } from './hooks/useReduxStateForCommunityIdeas';
import { useCommunityIdea } from './hooks/useCommunityIdea';
import type { Company } from './hooks/useCommunityIdea';
import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart';
import If from '@/components/If';
import useUser from '@/hooks/useUser';
import { useTracking } from 'react-tracking';
import Media from 'react-media';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { pluralise } from '@utilities/pluralise';
import HorizontallySlidingCompanyTile from '@/components/HorizontallySlidingCompanyTile';
import { useMount } from '@simplywallst/ui-core';
import { SWS_HOST } from '@/constants';
import DocumentHeader from '@/components/DocumentHeader';
import { getReadableCountry } from '@/utilities/markets';
import EndState from '@/pages/DiscoverPageV2/components/EndState';
import Footer from '@/components/Footer';
import { getGridViewCoverFromImageUrl } from '@/utilities/coverImages';
import { useCloudflareImage } from '@/hooks/useCloudflareImage';

const NarrativeRationalSlide = loadable(
  () =>
    import('@/components/HorizontallySlidingCompanyTile/NarrativeRationalSlide')
);

const RisksRewardsSlide = loadable(
  () => import('@/components/HorizontallySlidingCompanyTile/RisksRewardsSlide')
);

const AnalysisSlide = loadable(
  () => import('@/components/HorizontallySlidingCompanyTile/AnalysisSlide'),
  {
    ssr: false,
  }
);

const ResearchSlide = loadable(
  () => import('@/components/HorizontallySlidingCompanyTile/ResearchSlide'),
  {
    ssr: false,
  }
);

const GuestRegistrationModalOnScroll = loadable(
  () => import('@/components/GuestRegistrationModalOnScroll')
);

interface Tile extends Company {
  index: number;
  isFetching: boolean;
  preFetchCompany: (id: string) => void;
}

const MemoTile = memo((props: Tile) => (
  <HorizontallySlidingCompanyTile
    index={props.index}
    key={`community-idea-tile-${props.companyId}`}
    name={props.shortName}
    image={props.mainThumb}
    companyId={props.companyId}
    uniqueSymbol={props.uniqueSymbol}
    tickerSymbol={props.tickerSymbol}
    description={props.shortDescription}
    snowflakePoints={props.snowflakePoints}
    canonicalUrl={props.canonicalUrl}
    onLongHover={props.preFetchCompany}
  >
    <NarrativeRationalSlide
      tickerSymbol={props.tickerSymbol}
      innerHTML={props.narrativeHTML}
      isFetching={props.isFetching}
      canonicalUrl={props.canonicalUrl}
    />
    <RisksRewardsSlide
      statements={props.statements}
      riskKeys={props.riskKeys}
      rewardKeys={props.rewardKeys}
      hasRisks={props.hasRisks}
      hasRewards={props.hasRewards}
      tickerSymbol={props.tickerSymbol}
      canonicalUrl={props.canonicalUrl}
      isFetching={props.isFetching}
    />
    <AnalysisSlide
      companyId={props.companyId}
      canonicalUrl={props.canonicalUrl}
    />
    <ResearchSlide
      companyName={props.shortName}
      canonicalUrl={props.canonicalUrl}
    />
  </HorizontallySlidingCompanyTile>
));

const PAGE_SIZE = 6;

const CommunityIdea = () => {
  /**
   * Redux
   */
  const {
    id = '0',
    canonicalUrl,
    preFetchCompany,
  } = useReduxStateForCommunityIdeas();
  /**
   *
   */
  const theme = useTheme();
  const { isMobile, isTablet } = useUserAgent();
  const { Track, trackEvent } = useTracking({ type: 'track', subject: 'page' });

  useMount(() => {
    trackEvent({ action: 'landed', id });
  });

  const { data: user, isFetched: isUserFetched } = useUser();
  const demonymCountry = getReadableCountry(
    user.defaultMarketISOWithUsFallback
  );

  const { data, isFetching, isError } = useCommunityIdea(id);

  const LoadingState = useMemo(
    () =>
      Array.from({ length: PAGE_SIZE }, (_n, i) => (
        <HorizontallySlidingCompanyTile key={`loading-state-tile-${i}`}>
          <NarrativeRationalSlide />
          <RisksRewardsSlide />
          <AnalysisSlide />
        </HorizontallySlidingCompanyTile>
      )),
    []
  );
  const totalRecords = data?.companies?.length || 0;
  const companyCount = `${totalRecords} ${pluralise(
    totalRecords,
    'company',
    'companies'
  )}`;

  const openGraphImageUrl = useCloudflareImage({
    src: getGridViewCoverFromImageUrl(
      typeof data?.imageUrl !== 'undefined' ? data.imageUrl : null
    ),
    width: 1200,
    height: 630,
    fit: 'cover',
    quality: 30,
  });

  if (isError)
    return (
      <Track>
        <OneColumn>
          <Grid templateColumnsMd="1fr 1fr 1fr">
            <Grid columnMd="2/3">
              <Text typography="base" textAlign="center">
                We're sorry, but an error occured. Please try again later.
              </Text>
            </Grid>
          </Grid>
        </OneColumn>
      </Track>
    );
  return (
    <Track>
      <OneColumn>
        <DocumentHeader
          title={data?.seoTitle || null}
          allowCrawlers={data?.crawlable}
          description={data?.seoDescription}
          openGraphImageUrl={openGraphImageUrl}
        >
          <link rel="canonical" href={`${SWS_HOST}${canonicalUrl}`} />
        </DocumentHeader>
        <Header
          isFetching={isFetching}
          imageUrl={data?.imageUrl}
          title={data?.title}
          author={data?.author}
          authorUrl={data?.authorUrl}
          avatar={data?.authorImageUrl}
          innerHTML={data?.intro}
          breadCrumbs={data?.breadCrumbs}
        />
        <Grid
          templateColumnsMd="1fr"
          templateRows="48px"
          templateRowsLg="64px"
          alignItems="center"
        >
          <Box marginX="x2" marginXLg="x0">
            <Text typography="xSmall" color="whiteRGBA" opacity={0.5}>
              {isFetching ? <Bones maxWidth={280} /> : companyCount}
            </Text>
          </Box>
        </Grid>
        <MiniPriceChartDefinitions />
        {isFetching
          ? LoadingState
          : data?.companies?.map((n, i) => (
              <MemoTile
                key={n.companyId}
                index={i}
                isFetching={isFetching}
                preFetchCompany={preFetchCompany}
                {...n}
              />
            ))}
        <Disclaimer disclaimer={data?.disclaimer} />
        <EndState
          market={user.defaultMarketISOWithUsFallback}
          country={demonymCountry}
        />
        <If match={isUserFetched && user.loggedIn === false}>
          <Media
            query={{ maxWidth: theme.mdMaxRaw }}
            defaultMatches={isMobile || isTablet}
          >
            {(matches) => (
              <GuestRegistrationModalOnScroll
                pixelLimit={matches ? 2500 : 1000}
              />
            )}
          </Media>
        </If>
        <Footer />
      </OneColumn>
    </Track>
  );
};

export default CommunityIdea;
